import React, {useCallback, useContext, useEffect, useState} from 'react';
import {ModalsContext} from '../../modals/ModalsContext';
import {MODALS_TYPES} from '../../../../constants/ModalsConstants';
import {GetEventPlayersCall, MapPlayersCall} from "../../../../apis/player";
import {PlayerBookmakersStoredObject, PlayerMarketData} from '../../../../@types/response';
import {MapPlayerObject, MarketTable} from '../MarketTable';
import {useSelector} from "react-redux";
import {getPlayerMarkets} from "../../../../store/selectors/getPlayerMarkets";
import {EventModalProps} from '../../../../@types/inputs';
import {faSearch} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {PLAYER_DEFAULTS, PLAYER_DETAILS_MARKETS, PLAYER_MARKETS} from "../../../../constants/CommonConstants";
import {getMainSbv} from "../../../../store/utils/playerUtils";
import {getFixtures} from "../../../../store/selectors/getFixtures";

const MarketPlayerModal: React.FC = () => {
    const {modalsData, openedModals, setOpenedModalsCallback} = useContext(ModalsContext);
    const opening = openedModals.indexOf(MODALS_TYPES.PLAYER_MARKET_MODAL) !== -1
    const onCancelClickCb = useCallback(() => {
        setOpenedModalsCallback(MODALS_TYPES.PLAYER_MARKET_MODAL, false);
        resetModal();
    }, [setOpenedModalsCallback]);

    const playerMarkets: PlayerMarketData = useSelector(getPlayerMarkets);
    const fixtures = useSelector(getFixtures);

    const event: EventModalProps = (modalsData[MODALS_TYPES.PLAYER_MARKET_MODAL] || {}).event;
    const selectedMarketId: string = (modalsData[MODALS_TYPES.PLAYER_MARKET_MODAL] || {}).marketId || '';
    const selectedSignId: string = (modalsData[MODALS_TYPES.PLAYER_MARKET_MODAL] || {}).signId || '';

    const fixture = event?.id ? fixtures[event.id] : undefined;
    const sportId = fixture?.sportId ?? event?.sportId;

    const [mapMode, setMapMode] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [books, setBooks] = useState<PlayerBookmakersStoredObject>({});
    const [markets, setMarkets] = useState<any[]>([]);
    const [marketId, setMarketId] = useState<string>(selectedMarketId);
    const [signId, setSignId] = useState<string>(selectedSignId);
    const [mapClick, setMapClick] = useState<boolean>(false);
    const [sbvs, setSbvs] = useState<{ [playerId: string]: { [bookmaker: string]: string } }>({})

    const escFunction = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            setOpenedModalsCallback(MODALS_TYPES.PLAYER_MARKET_MODAL, false);
            resetModal();
        }
    }, [setOpenedModalsCallback]);

    useEffect(() => {
        setMarketId(selectedMarketId || '')
        setSignId(selectedSignId || '')
    }, [selectedMarketId, selectedSignId])

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    useEffect(() => {
        if (!opening || !sportId || !event?.id) return;
        const allowedMarkets = PLAYER_DETAILS_MARKETS[sportId] || PLAYER_DETAILS_MARKETS['1']
        const defaultMarketId = PLAYER_DEFAULTS[sportId]?.market || '';
        const defaultSignId = PLAYER_DEFAULTS[sportId]?.sign || '';
        const resolvedMarketId = marketId || defaultMarketId;
        const resolvedSignId = signId || defaultSignId;

        if (marketId !== resolvedMarketId) setMarketId(resolvedMarketId);
        if (signId !== resolvedSignId) setSignId(resolvedSignId);

        GetEventPlayersCall(event.id).then((result) => {
            setMarkets(Object.values(playerMarkets)
                    .filter(m => allowedMarkets.includes(m.id))
                    .flatMap(market =>
                            Object.entries(market.outcomes)
                                    .filter(([oId, _o]) => oId !== 'U')
                                    .map(([outcomeId, outcome]) => ({
                                        market: {id: market.id, name: market.name, sbv: market.sbv},
                                        outcome: {id: outcomeId, name: outcome.name}
                                    }))
                    ))
            setSbvs(getMainSbv(result, resolvedMarketId))
            setBooks(result)
        }, () => {
            setMarkets(Object.values(playerMarkets)
                    .filter(m => allowedMarkets.includes(m.id))
                    .flatMap(market =>
                            Object.entries(market.outcomes)
                                    .filter(([oId, _o]) => oId !== 'U')
                                    .map(([outcomeId, outcome]) => ({
                                        market: {id: market.id, name: market.name, sbv: market.sbv},
                                        outcome: {id: outcomeId, name: outcome.name}
                                    }))
                    ))
            setSbvs({})
            setBooks({})
        })
    }, [playerMarkets, opening, sportId, event, marketId, signId])

    const mapPlayers = (playersToMap: MapPlayerObject) => {
        MapPlayersCall(event?.id, Object.values(playersToMap)).then((result) => {
            setBooks(result)
        }, () => {
            setBooks(books)
        })
    }

    const resetButton = () => {
        setMapClick(false)
    }

    const resetModal = () => {
        setMapMode(false)
        setMarketId(selectedMarketId)
        setSignId(selectedSignId)
        setSearchValue('')
        setBooks({})
        setMarketId('')
        setSignId('')
    }

    const startDt = new Date(fixture?.date ?? event?.date ?? 0)
    return (
            (openedModals.indexOf(MODALS_TYPES.PLAYER_MARKET_MODAL) > -1 && (
                    <div className='fixed z-10 inset-0 overflow-y-auto'>
                        <div className='flex items-center justify-center min-h-screen text-center relative'>
                            <div className='fixed inset-0 bg-modal-layer transition-opacity' onClick={onCancelClickCb}/>
                            <div style={{maxWidth: '1536px', minWidth: '640px'}}
                                 className={`bg-eventDetail-primary rounded-lg text-left shadow-xl transform transition-all border-solid border-eventDetail-secondary border-2`}>
                                <button
                                        type='button'
                                        className='hover:bg-blue-600 hover:text-black hover:border-black absolute z-20 bg-eventDetail-primary -right-4 -top-4 text-eventDetail-event border border-eventDetail-event rounded-full h-8 w-8'
                                        onClick={onCancelClickCb}
                                >
                                    x
                                </button>
                                <div className='text-white px-4 py-2'>
                                    <div className='mt-2'>
                                        <div className='flex border-b pb-2'>
                                            <div style={{
                                                background: `url('${process.env.PUBLIC_URL}/static/img/logo_icon.svg')`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundPosition: 'center'
                                            }}
                                                 className='w-[60px] h-[60px] mb-2.5'></div>
                                            <div className='flex flex-col justify-end mb-2.5 ml-2.5'>
                                                <h2 className='mb-3 w-100'><span className='absolute right-4 top-1'>
                                                <button className={"2xl:text-monitorBase text-monitorSm py-1 font-normal block w-full whitespace-nowrap"}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            mapMode ? setMapMode(false) : setMapMode(true)
                                                        }}
                                                >
                                                    <label className="flex items-center cursor-pointer">
                                                        <div className="relative">
                                                            <div className="2xl:w-10 w-7 2xl:h-5 h-4 border-2 border-dropDown-buttonBorder rounded-full shadow-inner"/> 
                                                            <div className="dot absolute 2xl:w-3 2xl:h-3 w-2 h-2 bg-dropDown-buttonBorder rounded-full shadow left-1 bottom-1 transition"
                                                                 style={mapMode ? {
                                                                     transform: "translateX(160%) ",
                                                                     backgroundColor: "#BABABA"
                                                                 } : {backgroundColor: "#222222"}}
                                                            />
                                                        </div>
                                                        <div className="ml-3 text-white font-bold">MAP MODE</div>
                                                    </label>
                                                </button>
                                                    </span></h2>
                                                <h2>{event?.name?.substring(0, 40) || ''}<span
                                                        className='absolute right-4'>ID: {event.id}</span></h2>
                                                <h2 className='text-eventDetail-event font-medium'>{(fixture?.category || "").substring(0, 15)} - {fixture?.tournament} | {[startDt.getDate().toString().padStart(2, '0'), (startDt.getMonth() + 1).toString().padStart(2, '0'), (startDt.getFullYear()).toString().padStart(2, '0')].join('/')} {`${startDt.getHours().toString().padStart(2, '0')}:${startDt.getMinutes().toString().padStart(2, '0')}`}
                                                    {mapMode && (<button onClick={() => {
                                                        setMapClick(true)
                                                    }}
                                                                         className='absolute hover:text-eventDetail-event font-bold right-4 text-white rounded px-2 bg-input'>MAP</button>)}
                                                </h2>
                                            </div>
                                        </div>
                                        <div className='flex pb-6 mb-3 mt-2'>
                                            <h2>
                                                <div className='flex absolute'>
                                                    <input
                                                            className='2xl:text-monitorBase text-monitorSm text-white bg-input focus:outline-none rounded-xl p-1'
                                                            placeholder="filter players"
                                                            value={searchValue}
                                                            onChange={(e) => {
                                                                setSearchValue(e.target.value)
                                                            }}
                                                    />
                                                    <FontAwesomeIcon className='relative right-6 top-1 text-icon'
                                                                     icon={faSearch}/>
                                                </div>
                                                <select
                                                        className='text-black absolute right-4 mt-1'
                                                        onChange={(e) => {
                                                            let market = e.target.value.toString().split(' - ')[0]
                                                            let sign = e.target.value.toString().split(' - ')[1]
                                                            setMarketId(market)
                                                            setSignId(sign)
                                                            setSbvs(getMainSbv(books, market))
                                                        }}
                                                        value={marketId + ' - ' + signId}
                                                >
                                                    {markets.map((p) => <option
                                                            key={p.market.id + ' - ' + p.outcome.id}
                                                            value={p.market.id + ' - ' + p.outcome.id}
                                                    >{p.market.sbv ? (p.market.name) : (p.market.name + ' - ' + p.outcome.name)}</option>)}
                                                </select>
                                            </h2>
                                        </div>
                                        <div
                                                className='w-full overflow-x-scroll overflow-y-visible h-[520px] dropdown-hidden-scrollbar'
                                        >{marketId !== '' &&
                                                <MarketTable
                                                        marketId={marketId}
                                                        signId={signId}
                                                        odds={books}
                                                        mapMode={mapMode}
                                                        searchValue={searchValue}
                                                        mapClick={mapClick}
                                                        mapPlayers={mapPlayers}
                                                        mapClicked={resetButton}
                                                        average={(marketId && PLAYER_MARKETS[marketId]) ? (PLAYER_MARKETS[marketId].sbv === undefined) : true}
                                                        sbv={sbvs}
                                                />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
            )) ||
            null
    )
};
export default MarketPlayerModal;
